import { ArrowLeft, Check, FolderKanban, GitCommitHorizontal, History, PenLine, Plus, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useApp } from "@/lib/AppContext";
import { ApiError, type Entity, type EntityDossier, type SubjectRelation } from "@/lib/api";
import { formatDate, formatRelative } from "@/lib/format";
import { personColour, personInitials } from "@/lib/personAvatar";
import { Belief, sourceHref } from "@/components/Belief";
import { BeliefsEditor } from "@/components/BeliefEditor";
import { entityHref, RelationsPanel } from "@/components/RelationsPanel";

/**
 * UI-19 — the initiative dossier: the one place an initiative is worked on rather than
 * merely corrected. Mirrors UI-8 (the person dossier) rather than UI-11 (the generic entity
 * detail), for the reason FEATURES.md gives — an initiative is where the user is day to day.
 *
 * **Everything here is a direct edit.** The main goal, the secondary goals and the aliases
 * were already human-owned (UI-6 amended); what this page adds is the same for relations and
 * beliefs, through the WORLD-8 capabilities `api.assertRelation` and `api.assertBelief`
 * expose. There is no review gate on a direct edit — the edit *is* the review, the same
 * principle a rename or a re-description on any entity already stands on.
 *
 * **A belief here is added, never edited in place.** "Correct" on a current belief calls
 * `assertBelief` with `supersedesBeliefId` set — the old one closes and points forward, the
 * new one opens, exactly the way a subject is renamed (WORLD-3). A statement is not a text
 * field with autosave; it is a new belief that happens to make an old one obsolete, and the
 * history that produces is the same kind of trail synthesis leaves.
 */
export function InitiativeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { api, entities, refreshEntities } = useApp();

  const [dossier, setDossier] = useState<EntityDossier | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [showSuperseded, setShowSuperseded] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setDossier(await api.getEntityDossier(id));
      setProblem(null);
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "The dossier could not be read.");
    }
  }, [api, id]);

  useEffect(() => {
    setDossier(null);
    setProblem(null);
    setShowSuperseded(false);
    void load();
  }, [load]);

  const reload = useCallback(async () => {
    await load();
    refreshEntities();
  }, [load, refreshEntities]);

  if (problem) {
    return (
      <Frame>
        <p role="alert" className="text-[length:var(--text-sm)] text-[var(--warning)]">
          {problem}
        </p>
      </Frame>
    );
  }

  if (!dossier) {
    return (
      <Frame>
        <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">Loading…</p>
      </Frame>
    );
  }

  const { entity, page, git } = dossier;
  const current = page?.sections.reduce((total, section) => total + section.beliefs.length, 0) ?? 0;
  const candidates = entities.filter((other) => other.id !== entity.id);

  return (
    <Frame>
      <Link
        to="/initiatives"
        className="inline-flex w-fit items-center gap-1.5 text-[length:var(--text-xs)] text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
      >
        <ArrowLeft size={14} strokeWidth={2} />
        Initiatives
      </Link>

      <Header entity={entity} onChanged={reload} />
      <MainGoal entity={entity} onChanged={reload} />
      <SecondaryGoals entity={entity} onChanged={reload} />
      <Aliases entity={entity} onChanged={reload} />
      <WhoIsOnIt relations={page?.relations ?? []} />
      <RelationsPanel
        thisEntityId={entity.id}
        thisEntityName={entity.name}
        thisEntityKind={entity.type}
        relations={page?.relations ?? []}
        editable
        candidates={candidates}
        onChanged={reload}
      />

      <BeliefsEditor
        slug={dossier.slug}
        sections={page?.sections ?? []}
        hint="Progress, plans, requests and decisions — from sources, or typed in directly."
        empty="Nothing recorded yet. Add the first one below, or let it fill in as sources mention this initiative."
        onAssert={async (input) => {
          await api.assertBelief(entity.id, input);
          await reload();
        }}
      />

      {page && page.superseded.length > 0 && (
        <div className="flex flex-col gap-[var(--space-4)]">
          <button
            type="button"
            onClick={() => setShowSuperseded((shown) => !shown)}
            aria-expanded={showSuperseded}
            className="inline-flex w-fit items-center gap-1.5 rounded-full border border-dashed border-[var(--border-default)] px-3 py-1 text-[length:var(--text-xs)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
          >
            <History size={12} strokeWidth={2} />
            {showSuperseded ? "Hide superseded" : `Show superseded (${page.superseded.length})`}
          </button>

          {showSuperseded && (
            <section className="flex flex-col gap-[var(--space-4)] opacity-60">
              {page.superseded.map((belief) => (
                <Belief key={belief.id} belief={belief} slug={page.slug} />
              ))}
            </section>
          )}
        </div>
      )}

      {page && page.sources.length > 0 && (
        <section className="flex flex-col gap-[var(--space-3)]">
          <h2 className="font-semibold text-[var(--text-primary)]">Sources</h2>
          <ul className="flex flex-col gap-1.5">
            {page.sources.map((source) => (
              <li key={source.sourceMaterialId} className="text-[length:var(--text-sm)]">
                <Link
                  to={sourceHref(source.kind, source.sourceMaterialId)}
                  className="text-[var(--text-primary)] underline underline-offset-2 hover:text-[var(--vl-life)]"
                >
                  {source.title ?? "Untitled source"}
                </Link>
                <span className="text-[var(--text-muted)]">
                  {source.occurredOn ? ` · ${formatDate(source.occurredOn)}` : ""} · {source.insightCount}{" "}
                  {source.insightCount === 1 ? "insight" : "insights"}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {page && (
        <p className="flex flex-wrap items-center gap-1.5 border-t border-[var(--border-subtle)] pt-[var(--space-4)] text-[length:var(--text-2xs)] text-[var(--text-muted)]">
          <GitCommitHorizontal size={12} strokeWidth={2} />
          {git.remote ?? "No remote — the repository is local to the Server"}
          {git.commitSha ? (
            <>
              <span aria-hidden>·</span>
              <span className="font-mono">{git.commitSha.slice(0, 7)}</span>
              {git.committedAt && <span>{formatRelative(git.committedAt)}</span>}
            </>
          ) : (
            <>
              <span aria-hidden>·</span>
              <span>not published yet</span>
            </>
          )}
          {current > 0 && (
            <>
              <span aria-hidden>·</span>
              <Link to={`/world-view/${page.slug}`} className="underline underline-offset-2 hover:text-[var(--text-secondary)]">
                Open on the World view
              </Link>
            </>
          )}
        </p>
      )}
    </Frame>
  );
}

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex max-w-[1120px] flex-col gap-[var(--space-4)] px-[var(--space-8)] py-[var(--space-8)]">
      {children}
    </div>
  );
}

// ---------------------------------------------------------------- header

function Header({ entity, onChanged }: { entity: Entity; onChanged: () => Promise<void> }) {
  const { api } = useApp();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(entity.name);
  const [busy, setBusy] = useState(false);

  async function save() {
    const name = draft.trim();
    if (name.length === 0 || busy) return;
    setBusy(true);
    try {
      await api.updateEntity(entity.id, { name, description: entity.description ?? "", mainGoal: entity.mainGoal });
      setEditing(false);
      await onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex items-center gap-[var(--space-4)]">
      <span
        aria-hidden
        className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-2)] text-[var(--vl-virtual-soft)]"
      >
        <FolderKanban size={24} strokeWidth={2} />
      </span>

      <div className="min-w-0 flex-1">
        {editing ? (
          <div className="flex items-center gap-2">
            <input
              autoFocus
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") void save();
                if (event.key === "Escape") setEditing(false);
              }}
              aria-label="Initiative name"
              className="min-w-0 flex-1 rounded-[var(--radius-md)] border border-[var(--primary)] bg-[var(--surface-2)] px-3 py-1 text-[length:var(--text-2xl)] font-extrabold text-[var(--text-primary)] outline-none"
            />
            <button
              type="button"
              disabled={busy}
              onClick={() => void save()}
              className="inline-flex h-8 items-center gap-1.5 rounded-[var(--radius-md)] bg-[var(--primary)] px-3 text-[length:var(--text-sm)] font-medium text-[var(--primary-foreground)] disabled:opacity-50"
            >
              <Check size={14} strokeWidth={2.5} />
              Save
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2.5">
            <h1 className="text-[length:var(--text-2xl)] font-extrabold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
              {entity.name}
            </h1>
            <button
              type="button"
              onClick={() => {
                setDraft(entity.name);
                setEditing(true);
              }}
              aria-label="Rename initiative"
              className="flex h-6 w-6 items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-muted)] opacity-60 hover:opacity-100"
            >
              <PenLine size={12} strokeWidth={2} />
            </button>
            <span className="rounded-full border border-[var(--border-subtle)] px-2 py-0.5 text-[length:var(--text-2xs)] text-[var(--text-secondary)]">
              Initiative
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- main goal

function MainGoal({ entity, onChanged }: { entity: Entity; onChanged: () => Promise<void> }) {
  const { api } = useApp();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(entity.mainGoal ?? "");
  const [busy, setBusy] = useState(false);

  async function save() {
    if (busy) return;
    setBusy(true);
    try {
      await api.updateEntity(entity.id, {
        name: entity.name,
        description: entity.description ?? "",
        mainGoal: draft.trim(),
      });
      setEditing(false);
      await onChanged();
    } finally {
      setBusy(false);
    }
  }

  if (editing) {
    return (
      <Card>
        <div className="flex flex-col gap-2.5">
          <textarea
            autoFocus
            rows={2}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="What is this initiative trying to achieve?"
            aria-label="Main goal"
            className="w-full resize-y rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-2)] px-3 py-2.5 text-[length:var(--text-sm)] leading-relaxed text-[var(--text-primary)] outline-none focus:border-[var(--ring)]"
          />
          <div className="flex items-center gap-2">
            <span className="text-[length:var(--text-2xs)] text-[var(--text-muted)]">
              The one thing this initiative is running toward.
            </span>
            <span className="ml-auto flex gap-2">
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="h-8 rounded-[var(--radius-md)] px-3 text-[length:var(--text-sm)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => void save()}
                className="inline-flex h-8 items-center gap-1.5 rounded-[var(--radius-md)] bg-[var(--primary)] px-3 text-[length:var(--text-sm)] font-medium text-[var(--primary-foreground)] disabled:opacity-50"
              >
                <Check size={14} strokeWidth={2.5} />
                Save
              </button>
            </span>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex items-start gap-[var(--space-4)]">
        <div className="min-w-0 flex-1">
          {entity.mainGoal ? (
            <p className="leading-relaxed text-[var(--text-secondary)]">{entity.mainGoal}</p>
          ) : (
            <p className="text-[length:var(--text-sm)] text-[var(--text-muted)]">No main goal yet.</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => {
            setDraft(entity.mainGoal ?? "");
            setEditing(true);
          }}
          aria-label="Edit main goal"
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-transparent px-2 py-0.5 text-[length:var(--text-2xs)] text-[var(--text-muted)] opacity-60 transition hover:border-[var(--border-subtle)] hover:text-[var(--vl-bridge-soft)] hover:opacity-100"
        >
          <PenLine size={12} strokeWidth={2} />
          Edit
        </button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------- secondary goals

function SecondaryGoals({ entity, onChanged }: { entity: Entity; onChanged: () => Promise<void> }) {
  const { api } = useApp();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");

  async function set(goals: string[]) {
    await api.setInitiativeSecondaryGoals(entity.id, goals);
    await onChanged();
  }

  return (
    <Section title="Secondary goals" hint="Alongside the main goal, in the order they matter.">
      <div className="flex flex-col gap-1.5">
        {entity.secondaryGoals.map((goal, index) => (
          <div
            key={`${index}-${goal}`}
            className="flex items-center gap-2.5 rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] px-3.5 py-2 text-[length:var(--text-sm)]"
          >
            <span className="min-w-0 flex-1 text-[var(--text-primary)]">{goal}</span>
            <button
              type="button"
              aria-label={`Remove ${goal}`}
              onClick={() => void set(entity.secondaryGoals.filter((_, i) => i !== index))}
              className="flex h-5 w-5 shrink-0 items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-muted)] opacity-60 hover:text-[var(--destructive)] hover:opacity-100"
            >
              <X size={12} strokeWidth={2} />
            </button>
          </div>
        ))}

        {adding ? (
          <input
            autoFocus
            value={draft}
            aria-label="Secondary goal"
            placeholder="A secondary goal, then Enter"
            onChange={(event) => setDraft(event.target.value)}
            onBlur={() => {
              setAdding(false);
              setDraft("");
            }}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                setAdding(false);
                setDraft("");
              }
              if (event.key !== "Enter" || draft.trim().length === 0) return;
              void set([...entity.secondaryGoals, draft.trim()]);
              setAdding(false);
              setDraft("");
            }}
            className="h-9 rounded-[var(--radius-md)] border border-[var(--primary)] bg-[var(--surface-2)] px-3 text-[length:var(--text-sm)] text-[var(--text-primary)] outline-none"
          />
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex w-fit items-center gap-1.5 rounded-full border border-dashed border-[var(--border-default)] px-2.5 py-1 text-[length:var(--text-xs)] text-[var(--text-muted)] hover:border-[var(--primary)] hover:text-[var(--vl-bridge-soft)]"
          >
            <Plus size={12} strokeWidth={2} />
            Add secondary goal
          </button>
        )}
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------- aliases

function Aliases({ entity, onChanged }: { entity: Entity; onChanged: () => Promise<void> }) {
  const { api } = useApp();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [problem, setProblem] = useState<string | null>(null);

  async function set(aliases: string[]) {
    try {
      await api.setEntityAliases(entity.id, aliases);
      setProblem(null);
      await onChanged();
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "The aliases could not be saved.");
    }
  }

  return (
    <Section title="Aliases" hint="Other names and spellings that resolve to this initiative.">
      <div className="flex flex-wrap items-center gap-1.5">
        {entity.aliases.map((alias) => (
          <span
            key={alias}
            className="inline-flex items-center gap-1 rounded-full bg-[var(--secondary)] py-0.5 pl-2.5 pr-1 text-[length:var(--text-xs)] text-[var(--secondary-foreground)]"
          >
            {alias}
            <button
              type="button"
              aria-label={`Remove ${alias}`}
              onClick={() => void set(entity.aliases.filter((other) => other !== alias))}
              className="flex h-4 w-4 items-center justify-center rounded-full text-[var(--text-muted)] opacity-60 hover:text-[var(--destructive)] hover:opacity-100"
            >
              <X size={12} strokeWidth={2} />
            </button>
          </span>
        ))}

        {adding ? (
          <input
            autoFocus
            value={draft}
            aria-label="Alias"
            placeholder="Alias, then Enter"
            onChange={(event) => setDraft(event.target.value)}
            onBlur={() => {
              setAdding(false);
              setDraft("");
            }}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                setAdding(false);
                setDraft("");
              }
              if (event.key !== "Enter" || draft.trim().length === 0) return;
              void set([...entity.aliases, draft.trim()]);
              setAdding(false);
              setDraft("");
            }}
            className="w-[150px] rounded-full border border-[var(--primary)] bg-[var(--surface-2)] px-2.5 py-0.5 text-[length:var(--text-xs)] text-[var(--text-primary)] outline-none"
          />
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-[var(--border-default)] px-2.5 py-0.5 text-[length:var(--text-xs)] text-[var(--text-muted)] hover:border-[var(--primary)] hover:text-[var(--vl-bridge-soft)]"
          >
            <Plus size={12} strokeWidth={2} />
            Add alias
          </button>
        )}
      </div>

      {problem && (
        <p role="alert" className="mt-2 text-[length:var(--text-xs)] text-[var(--warning)]">
          {problem}
        </p>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------- who's on it (WORLD-10)

/**
 * WORLD-10's second view: who leads this initiative and who is a member of it, drawn from the
 * `leads` and `member_of` rows and nothing else — the same rows the Relations panel below lists
 * in full, gathered into the one question somebody opening an initiative asks first.
 */
function WhoIsOnIt({ relations }: { relations: SubjectRelation[] }) {
  const leads = relations.filter((relation) => relation.type === "leads" && !relation.isSubject);
  const members = relations.filter((relation) => relation.type === "member_of" && !relation.isSubject);

  return (
    <Section title="Who's on it" hint="Drawn from who leads it and who is a member of it.">
      {leads.length === 0 && members.length === 0 ? (
        <p className="text-[length:var(--text-sm)] text-[var(--text-muted)]">
          Nobody yet. Add a <span className="text-[var(--text-secondary)]">leads</span> or{" "}
          <span className="text-[var(--text-secondary)]">has as a member</span> relation below.
        </p>
      ) : (
        <div className="flex flex-wrap gap-[var(--space-6)]">
          <People label="Leads" relations={leads} />
          <People label="Members" relations={members} />
        </div>
      )}
    </Section>
  );
}

function People({ label, relations }: { label: string; relations: SubjectRelation[] }) {
  if (relations.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      <span className="text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
        {label}
      </span>
      <div className="flex flex-wrap gap-1.5">
        {relations.map((relation) => (
          <Link
            key={relation.id}
            to={entityHref(relation.otherEntityKind, relation.otherEntityId)}
            className="inline-flex items-center gap-2 rounded-full border border-[var(--border-subtle)] bg-[var(--surface-glass)] py-1 pl-1 pr-3 text-[length:var(--text-sm)] text-[var(--text-primary)] hover:border-[var(--border-default)]"
          >
            <span
              aria-hidden
              className="flex h-6 w-6 items-center justify-center rounded-full text-[length:var(--text-2xs)] font-bold text-[var(--text-inverse)]"
              style={{ background: personColour(relation.otherEntityId) }}
            >
              {personInitials(relation.otherEntityName)}
            </span>
            {relation.otherEntityName}
            {relation.assertedByUser && (
              <PenLine size={11} strokeWidth={2} aria-label="Asserted by you" className="text-[var(--vl-bridge-soft)]" />
            )}
          </Link>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- small pieces

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section>
      <div className="mb-2 flex flex-wrap items-baseline gap-2.5">
        <h2 className="font-semibold text-[var(--text-primary)]">{title}</h2>
        {hint && <span className="text-[length:var(--text-xs)] text-[var(--text-muted)]">{hint}</span>}
      </div>
      {children}
    </section>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] p-[var(--space-4)]">
      {children}
    </div>
  );
}
